import { Translation } from '../providers/types/Translation';
import moment from 'moment/moment';
import { UserPolicy } from '../providers/types/UserPolicy';
import { TeamMemberPolicy } from '../providers/types/TeamMemberPolicy';
import { ActionTypes } from '../providers/types/ResourceAction';
import { ResourceType } from '../providers/types/Resource';
import { PixelCrop } from 'react-image-crop';
import { useEffect, DependencyList } from 'react';

export const getTranslation = (
  language: 'en' | 'fr',
  translation: Translation
) => {
  return translation[language]
    ? translation[language]?.translation
    : translation['fr']
    ? translation['fr'].translation
    : translation['en']
    ? translation['en'].translation
    : '';
};

export const copyObject = (a: any) => {
  return JSON.parse(JSON.stringify(a));
};

export function alphaSortObjectArray(array: any[], sortKey: string) {
  return array.sort((a, b) => {
    let nameA = a[sortKey].toUpperCase(); // ignore upper and lowercase
    let nameB = b[sortKey].toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
}

export function numericSortObjectArray(array: any[], sortKey: string) {
  return array.sort((a, b) => {
    if (a[sortKey] < b[sortKey]) {
      return -1;
    }
    if (a[sortKey] > b[sortKey]) {
      return 1;
    }
    return 0;
  });
}

export function dateSortObjectArray(array: any[], sortKey: string) {
  return array.sort((a, b) => {
    if (moment(a[sortKey]).isBefore(moment(b[sortKey]))) {
      return -1;
    }
    if (moment(b[sortKey]).isBefore(moment(a[sortKey]))) {
      return 1;
    }
    return 0;
  });
}

export const hasSystemPolicy = (
  policyName: string,
  userPolicies: UserPolicy[]
) => {
  if (userPolicies) {
    if (userPolicies.find((u) => u.policy_definition?.name === 'FCC_Admin')) {
      return true;
    }

    return !!userPolicies.find((u) => u.policy_definition?.name === policyName);
  }

  return false;
};

export const hasTeamPolicy = (
  policyName: string,
  teamId: number,
  teamPolicies: TeamMemberPolicy[]
) => {
  if (teamPolicies) {
    if (
      teamPolicies.find(
        (t) =>
          t.policy_definition?.name === 'FCC_TeamAdmin' && t.team_id === teamId
      )
    ) {
      return true;
    }

    return !!teamPolicies.find(
      (t) => t.policy_definition?.name === policyName && t.team_id === teamId
    );
  }

  return false;
};

export const getActivityIcon = (action: ActionTypes) => {
  switch (action) {
    case 'create':
      return 'file outline';
    case 'check_out':
      return 'download';
    case 'check_in':
      return 'upload';
    case 'update':
      return 'file alternate';
    case 'destroy':
      return 'delete';
    default:
      return 'trash';
  }
};

export const getResourceIcon = (type: ResourceType | 'config') => {
  switch (type) {
    case 'config':
      return 'configure';
    case 'header':
      return 'heading';
    case 'teidoc':
      return 'table';
    case 'facs':
      return 'id card';
    case 'image':
      return 'image';
    case 'sourceDoc':
      return 'file code outline';
    case 'standOff':
      return 'archive';
    case 'text':
    default:
      return 'file word';
  }
};

const TO_RADIANS = Math.PI / 180;

export async function canvasPreview(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  crop: PixelCrop,
  scale = 1,
  rotate = 0
) {
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  // devicePixelRatio slightly increases sharpness on retina devices
  // at the expense of slightly slower render times and needing to
  // size the image back down if you want to download/upload and be
  // true to the images natural size.
  const pixelRatio = window.devicePixelRatio;
  // const pixelRatio = 1

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  // 5) Move the crop origin to the canvas origin (0,0)
  ctx.translate(-cropX, -cropY);
  // 4) Move the origin to the center of the original position
  ctx.translate(centerX, centerY);
  // 3) Rotate around the origin
  ctx.rotate(rotateRads);
  // 2) Scale the image
  ctx.scale(scale, scale);
  // 1) Move the center of the image to the origin (0,0)
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight
  );

  ctx.restore();
}

let previewUrl = '';

function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve) => {
    // @ts-ignore
    canvas.toBlob(resolve);
  });
}

// Returns an image source you should set to state and pass
// `{previewSrc && <img alt="Crop preview" src={previewSrc} />}`
export async function imgPreview(
  image: HTMLImageElement,
  crop: PixelCrop,
  scale = 1,
  rotate = 0
) {
  const canvas = document.createElement('canvas');
  await canvasPreview(image, canvas, crop, scale, rotate);

  const blob = await toBlob(canvas);
  if (previewUrl) {
    URL.revokeObjectURL(previewUrl);
  }

  previewUrl = URL.createObjectURL(blob);
  return previewUrl;
}

export function useDebounceEffect(
  fn: () => void,
  waitTime: number,
  deps?: DependencyList
) {
  useEffect(() => {
    const t = setTimeout(() => {
      fn.apply(undefined, deps as []);
    }, waitTime);

    return () => {
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps as DependencyList);
}

export async function toImageURL(
  image: HTMLImageElement,
  crop: PixelCrop,
  scale = 1,
  rotate = 0
) {
  const canvas = document.createElement('canvas');
  await canvasPreview(image, canvas, crop, scale, rotate);

  return canvas.toDataURL('image/jpeg', 1.0);
}

export function imageURIToBlob(imageURI: string) {

  // Create Blob file from URL
  function blobCreationFromURL(inputURI: string) {
    let binaryVal;

    // mime extension extraction
    let inputMIME = inputURI.split(',')[0].split(':')[1].split(';')[0];

    // Extract remaining part of URL and convert it to binary value
    if (inputURI.split(',')[0].indexOf('base64') >= 0) {
      binaryVal = atob(inputURI.split(',')[1]);
    } else {
      // Decoding of base64 encoded string
      binaryVal = unescape(inputURI.split(',')[1]);
    }

    // Computation of new string in which hexadecimal
    // escape sequences are replaced by the character
    // it represents

    // Store the bytes of the string to a typed array
    let blobArray = [];
    for (let index = 0; index < binaryVal.length; index++) {
      blobArray.push(binaryVal.charCodeAt(index));
    }

    // @ts-ignore
    return new Blob([blobArray], {
      type: inputMIME,
    });
  }

  return blobCreationFromURL(imageURI);
}

export function b64toBlob(b64Data: string) {
  let block = b64Data.split(";");
  // Get the content type
  const contentType = block[0].split(":")[1];
  // get the real base64 content of the file
  const realData = block[1].split(",")[1];

  const sliceSize = 512;

  const byteCharacters = atob(realData);
  let byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    let slice = byteCharacters.slice(offset, offset + sliceSize);

    let byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    let byteArray = new Uint8Array(byteNumbers);

    byteArrays.push(byteArray);
  }
  return new Blob(byteArrays, {type: contentType});
}
